import { queryView, type QueryResultLike } from '@krp/client-runtime'
import { WEEKDAYS_SHORT, addDays, firstOfMonth, formatDayFull, formatMonth, lastOfMonth, spanDays, weekdayIndex, type ChoiceChip, type SelectOption } from '@krp/ui-kit'

import type { OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import { formatDateTime, formatMoney, formatTime } from '../../pricing/model/format'
import { nodeExists, registerKeysOf, selectionText, storeChoicesOf, treeNodesOf } from '../../receipts/model/tree'
import type { CashReceiptsNavVm, CashRegisters } from '../../receipts/model/types'
import {
  EMPTY_SHIFTS_VIEW,
  type CashShift,
  type CashShiftDayVm,
  type CashShiftRowVm,
  type CashShifts,
  type CashShiftsArgs,
  type CashShiftsIntentState,
  type CashShiftsScreenVm,
  type CashShiftsViewState,
} from './types'

export type CashShiftsResult = QueryResultLike<OneCEnvelope<CashShifts>>
export type CashShiftRegistersResult = QueryResultLike<OneCEnvelope<CashRegisters>>

export function cashShiftsViewOf(state: CashShiftsIntentState, resourceId: string): CashShiftsViewState {
  return state[normalizeRid(resourceId)] ?? EMPTY_SHIFTS_VIEW
}

/** Показанный месяц: выбранный человеком или текущий. */
export const shiftsMonthOf = (view: CashShiftsViewState, today: string): string => view.month ?? firstOfMonth(today)

/** Смены читаются за весь показанный месяц по всей базе: место, день, кассир и состояние отбирает экран, без нового запроса к 1С. */
export function cashShiftsArgsOf(resourceId: string, view: CashShiftsViewState, today: string): CashShiftsArgs | null {
  if (resourceId === '') return null
  const month = shiftsMonthOf(view, today)
  return { resourceId, from: month, to: lastOfMonth(month) }
}

/** День смены: дата открытия, а если её база не отдала — дата документа смены. */
export const shiftDayOf = (shift: CashShift): string | null => (shift.openedAt ?? shift.date)?.slice(0, 10) ?? null

const STATE_LABELS = { open: 'Открыта', closed: 'Закрыта' } as const

function rowOf(shift: CashShift): CashShiftRowVm {
  const totals =
    shift.receiptCount === null
      ? null
      : [`чеков ${shift.receiptCount}`, shift.income === null ? null : `приход ${formatMoney(shift.income)}`, shift.refunds === null ? null : `возвраты ${formatMoney(shift.refunds)}`]
          .filter((part) => part !== null)
          .join(' · ')
  return {
    id: shift.key,
    title: shift.shiftNumber !== null ? `Смена № ${shift.shiftNumber}` : 'Смена без номера',
    register: shift.registerName ?? 'Касса не указана',
    cashier: shift.cashierName ?? 'Кассир не определён',
    opened: shift.openedAt !== null || shift.date !== null ? formatDateTime(shift.openedAt ?? shift.date) : '—',
    closed: shift.closedAt !== null ? formatDateTime(shift.closedAt) : shift.state === 'open' ? 'ещё открыта' : '—',
    state: shift.state,
    stateLabel: shift.state === null ? 'Состояние не известно' : STATE_LABELS[shift.state],
    note:
      [
        shift.status === null || shift.status === '' ? null : `статус в 1С: ${shift.status}`,
        shift.number === null ? null : `документ ${shift.number}`,
        shift.posted ? null : 'не проведён',
        totals,
      ]
        .filter((part) => part !== null)
        .join(' · ') || null,
  }
}

const countByRegister = (shifts: readonly CashShift[]): Map<string, number> => {
  const counts = new Map<string, number>()
  for (const shift of shifts) if (shift.registerKey !== null) counts.set(shift.registerKey, (counts.get(shift.registerKey) ?? 0) + 1)
  return counts
}

const plural = (count: number, one: string, few: string, many: string): string => {
  const rest = count % 100
  if (rest >= 11 && rest <= 14) return many
  const last = count % 10
  return last === 1 ? one : last >= 2 && last <= 4 ? few : many
}

export interface CashShiftsScreenInput {
  resourceId: string
  today: string
  intent: CashShiftsIntentState
  registers: CashShiftRegistersResult
  shifts: CashShiftsResult
}

/**
 * Экран «Смены 1С»: смены показанного месяца, отобранные местом дерева, днём, кассиром и состоянием. Число смен у места, дня и
 * состояния — по тем же сменам месяца, так что выбор не ждёт нового запроса к 1С.
 */
export function cashShiftsScreenVmOf(input: CashShiftsScreenInput): CashShiftsScreenVm {
  const { resourceId, today, intent, registers, shifts } = input
  const view = cashShiftsViewOf(intent, resourceId)
  const directory = registers.data?.data
  const month = shiftsMonthOf(view, today)
  const all = shifts.data?.data.items ?? []

  const selectedId = nodeExists(directory, view.nodeId) ? view.nodeId : ''
  const placeKeys = selectedId === '' ? null : new Set(registerKeysOf(directory, selectedId))
  const inPlace = (shift: CashShift) => placeKeys === null || (shift.registerKey !== null && placeKeys.has(shift.registerKey))
  const byCashier = (shift: CashShift) => view.cashier === '' || shift.cashierKey === view.cashier
  const byState = (shift: CashShift) => view.state === '' || shift.state === view.state
  const byDay = (shift: CashShift) => view.day === null || shiftDayOf(shift) === view.day

  const registersQuery = queryView(registers, (value) => value.data.registers.length === 0)
  const counts = shifts.data === undefined ? null : countByRegister(all.filter((shift) => byCashier(shift) && byState(shift) && byDay(shift)))
  const nav: CashReceiptsNavVm = {
    query: registersQuery,
    nodes: treeNodesOf(directory, view.collapsed, selectedId, counts),
    stores: storeChoicesOf(directory, counts),
    selectedId,
    summaryText: selectionText(directory, selectedId),
    note: registersQuery.error === null ? null : `Кассы не прочитаны: ${registersQuery.error.message}. Смены показываются по всей базе, выбрать место нельзя.`,
  }

  const placed = all.filter(inPlace)
  const perDay = new Map<string, number>()
  for (const shift of placed.filter((item) => byCashier(item) && byState(item))) {
    const day = shiftDayOf(shift)
    if (day !== null) perDay.set(day, (perDay.get(day) ?? 0) + 1)
  }
  const days: CashShiftDayVm[] = Array.from({ length: spanDays(month, lastOfMonth(month)) }, (_, index) => {
    const day = addDays(month, index)
    const count = perDay.get(day) ?? 0
    return {
      day,
      date: String(Number(day.slice(8, 10))),
      weekday: WEEKDAYS_SHORT[weekdayIndex(day)] ?? '',
      count,
      selected: view.day === day,
      today: day === today,
      future: day > today,
      label: `${formatDayFull(day)}: ${count === 0 ? 'смен нет' : `${count} ${plural(count, 'смена', 'смены', 'смен')}`}`,
    }
  })

  const cashierNames = new Map<string, string>()
  for (const shift of placed) if (shift.cashierKey !== null) cashierNames.set(shift.cashierKey, shift.cashierName ?? 'Кассир без имени')
  const cashiers: SelectOption[] = [
    { value: '', label: 'Все кассиры' },
    ...[...cashierNames.entries()].sort((left, right) => left[1].localeCompare(right[1], 'ru')).map(([value, label]) => ({ value, label })),
  ]
  if (view.cashier !== '' && !cashierNames.has(view.cashier)) cashiers.push({ value: view.cashier, label: 'Кассир не найден в этом месяце' })

  const forStates = placed.filter((shift) => byCashier(shift) && byDay(shift))
  const states: ChoiceChip[] = [
    { id: '', label: 'Все', count: forStates.length },
    { id: 'open', label: 'Открытые', count: forStates.filter((shift) => shift.state === 'open').length },
    { id: 'closed', label: 'Закрытые', count: forStates.filter((shift) => shift.state === 'closed').length },
  ]

  const visible = forStates.filter(byState)
  const query = queryView(shifts, (value) => value.data.items.length === 0)
  return {
    nav,
    month,
    monthTitle: formatMonth(month),
    previousMonth: firstOfMonth(addDays(month, -1)),
    nextMonth: addDays(lastOfMonth(month), 1),
    canNext: month < firstOfMonth(today),
    days,
    day: view.day,
    states,
    state: view.state,
    cashiers,
    cashier: view.cashier,
    query,
    countText: `${view.day === null ? 'За месяц' : `За ${formatDayFull(view.day)}`}: ${visible.length} ${plural(visible.length, 'смена', 'смены', 'смен')}`,
    readAt: shifts.data === undefined ? null : `Прочитано из 1С в ${formatTime(shifts.data.data.observedAt)}`,
    truncatedNote: shifts.data?.data.truncated === true ? 'В 1С за месяц смен больше, чем служба отдаёт за раз: показаны самые новые.' : null,
    filteredOut: query.status === 'ready' && visible.length === 0,
    rows: visible.map(rowOf),
  }
}
